import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Space,
  message,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { subscriptionAdminAPI } from "../../../utils/api/subscriptionAPI";
import axios from "axios";
import { BASE_API_URL } from "../../../utils/consts";
import dayjs from "dayjs";
import "./SubscriptionsManagement.scss";

interface Client {
  _id: string;
  name: string;
  clientName?: string; // Added for backend compatibility
  email?: string; // Made optional since it doesn't exist in backend model
}

interface Plan {
  _id: string;
  name: string;
  billingModel: string;
  basePrice: number;
  pricePerEmployee: number;
  currency: string;
}

interface Subscription {
  _id: string;
  clientId: {
    _id: string;
    name: string;
  };
  planId: {
    _id: string;
    name: string;
  };
  status: string;
  startDate: string;
  totalMonthsPaid: number;
  totalRevenue: number;
  isAccessBlocked: boolean;
}

const SubscriptionsManagement: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isStartDateModalVisible, setIsStartDateModalVisible] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
  const [form] = Form.useForm();
  const [startDateForm] = Form.useForm();

  useEffect(() => {
    fetchSubscriptions();
    fetchClients();
    fetchPlans();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await subscriptionAdminAPI.getAllSubscriptions();
      if (response.data && response.data.data) {
        setSubscriptions(response.data.data.subscriptions || []);
      }
    } catch (error: any) {
      console.error("Error fetching subscriptions:", error);
      message.error("Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem("x-access-token");
      const response = await axios.get(`${BASE_API_URL}/clients`, {
        headers: { "x-access-token": token },
      });
      setClients(response.data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await subscriptionAdminAPI.getAllPlans({
        isActive: true,
      });
      if (response.data && response.data.data) {
        // Backend returns data directly as array, not wrapped in { plans: [] }
        const plansData = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.data.plans || [];
        setPlans(plansData);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const handleCreateSubscription = () => {
    setSelectedSubscription(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditSubscription = (record: Subscription) => {
    setSelectedSubscription(record);
    form.setFieldsValue({
      clientId: record.clientId._id,
      planId: record.planId._id,
      startDate: dayjs(record.startDate),
      status: record.status,
    });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        ...values,
        startDate: values.startDate.format("YYYY-MM-DD"),
      };

      if (selectedSubscription) {
        // Check if plan changed
        const planChanged = values.planId !== selectedSubscription.planId._id;

        if (planChanged) {
          // Use changePlan endpoint for plan changes
          await subscriptionAdminAPI.changePlan(
            selectedSubscription._id,
            values.planId,
            true // prorated
          );
          message.success("Plan changed successfully!");
        }

        // Update other fields (if any changed)
        const { planId, ...otherUpdates } = payload;
        if (Object.keys(otherUpdates).length > 1) {
          // More than just startDate
          await subscriptionAdminAPI.updateSubscription(
            selectedSubscription._id,
            otherUpdates
          );
          if (!planChanged) {
            message.success("Subscription updated successfully!");
          }
        } else if (!planChanged) {
          message.info("No changes detected");
        }
      } else {
        // Create new subscription
        await subscriptionAdminAPI.createSubscription(payload);
        message.success("Subscription created successfully!");
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchSubscriptions();
    } catch (error: any) {
      console.error("Error saving subscription:", error);
      message.error(
        error.response?.data?.message || "Failed to save subscription"
      );
    }
  };

  const handleBlockAccess = async (
    subscriptionId: string,
    currentlyBlocked: boolean
  ) => {
    try {
      if (currentlyBlocked) {
        await subscriptionAdminAPI.unblockAccess(subscriptionId);
        message.success("Access unblocked successfully!");
      } else {
        await subscriptionAdminAPI.blockAccess(
          subscriptionId,
          "Blocked by admin"
        );
        message.success("Access blocked successfully!");
      }
      fetchSubscriptions();
    } catch (error: any) {
      message.error("Failed to update access status");
    }
  };

  const handlePauseResume = async (
    subscriptionId: string,
    currentStatus: string
  ) => {
    try {
      if (currentStatus === "PAUSED") {
        await subscriptionAdminAPI.resumeSubscription(subscriptionId);
        message.success("Subscription resumed!");
      } else {
        await subscriptionAdminAPI.pauseSubscription(subscriptionId);
        message.success("Subscription paused!");
      }
      fetchSubscriptions();
    } catch (error: any) {
      message.error("Failed to update subscription status");
    }
  };

  const handleDeleteSubscription = async (subscriptionId: string) => {
    try {
      await subscriptionAdminAPI.deleteSubscription(subscriptionId);
      message.success("Subscription permanently deleted!");
      fetchSubscriptions();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Failed to delete subscription"
      );
    }
  };

  const handleRegeneratePeriods = async (subscriptionId: string) => {
    try {
      const response = await subscriptionAdminAPI.regenerateBillingPeriods(
        subscriptionId
      );
      const count = response.data?.data?.length || 0;
      message.success(`Successfully regenerated ${count} billing periods!`);
      fetchSubscriptions();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Failed to regenerate billing periods"
      );
    }
  };

  const handleOpenStartDateModal = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    startDateForm.setFieldsValue({
      startDate: dayjs(subscription.startDate),
    });
    setIsStartDateModalVisible(true);
  };

  const handleChangeStartDate = async () => {
    try {
      const values = await startDateForm.validateFields();
      const newStartDate = values.startDate.format("YYYY-MM-DD");

      await subscriptionAdminAPI.updateSubscription(selectedSubscription._id, {
        startDate: newStartDate,
      });

      message.success(
        "Start date changed! Billing periods will be regenerated."
      );
      setIsStartDateModalVisible(false);
      fetchSubscriptions();
    } catch (error: any) {
      message.error(
        error.response?.data?.message || "Failed to change start date"
      );
    }
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      ACTIVE: "green",
      TRIAL: "blue",
      PAUSED: "orange",
      CANCELLED: "red",
      OVERDUE: "red",
    };
    return colors[status] || "default";
  };

  const columns = [
    {
      title: "Client",
      dataIndex: ["clientId", "name"],
      key: "client",
      sorter: (a: Subscription, b: Subscription) =>
        a.clientId.name.localeCompare(b.clientId.name),
    },
    {
      title: "Plan",
      dataIndex: ["planId", "name"],
      key: "plan",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string, record: Subscription) => (
        <Space>
          <Tag color={getStatusColor(status)}>{status}</Tag>
          {record.isAccessBlocked && <Tag color="red">BLOCKED</Tag>}
        </Space>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
    },
    {
      title: "Months Paid",
      dataIndex: "totalMonthsPaid",
      key: "totalMonthsPaid",
      align: "center" as const,
    },
    {
      title: "Total Revenue",
      dataIndex: "totalRevenue",
      key: "totalRevenue",
      render: (revenue: number) => `€${revenue.toFixed(2)}`,
      align: "right" as const,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Subscription) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() =>
              window.open(`/admin/billing/${record.clientId._id}`, "_blank")
            }
          >
            Billing
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditSubscription(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title={record.isAccessBlocked ? "Unblock access?" : "Block access?"}
            onConfirm={() =>
              handleBlockAccess(record._id, record.isAccessBlocked)
            }
          >
            <Button type="link" danger={!record.isAccessBlocked}>
              {record.isAccessBlocked ? "Unblock" : "Block"}
            </Button>
          </Popconfirm>
          <Popconfirm
            title={
              record.status === "PAUSED"
                ? "Resume subscription?"
                : "Pause subscription?"
            }
            onConfirm={() => handlePauseResume(record._id, record.status)}
          >
            <Button type="link">
              {record.status === "PAUSED" ? "Resume" : "Pause"}
            </Button>
          </Popconfirm>
          <Button
            type="link"
            icon={<CalendarOutlined />}
            onClick={() => handleOpenStartDateModal(record)}
          >
            Change Start Date
          </Button>
          <Popconfirm
            title="Regenerate all billing periods?"
            description="This will recalculate all billing periods from the subscription start date."
            onConfirm={() => handleRegeneratePeriods(record._id)}
            okText="Regenerate"
          >
            <Button type="link" icon={<ReloadOutlined />}>
              Regenerate
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Permanently delete subscription?"
            description="This will delete all billing periods and payments. This action cannot be undone."
            onConfirm={() => handleDeleteSubscription(record._id)}
            okText="Delete"
            okType="danger"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="subscriptions-management">
      <div className="page-header">
        <div className="header-title">
          <CreditCardOutlined
            style={{ fontSize: "24px", marginRight: "12px" }}
          />
          <h2>Subscriptions Management</h2>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateSubscription}
        >
          Create Subscription
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={subscriptions}
        loading={loading}
        rowKey="_id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} subscriptions`,
        }}
      />

      <Modal
        title={
          selectedSubscription ? "Edit Subscription" : "Create New Subscription"
        }
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="clientId"
            label="Client"
            rules={[{ required: true, message: "Please select a client" }]}
          >
            <Select
              placeholder="Select client"
              showSearch
              filterOption={(input, option) =>
                String(option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              disabled={!!selectedSubscription}
              options={clients.map((client) => ({
                label: `${client.name} (${client.clientName || "N/A"})`,
                value: client._id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="planId"
            label="Plan"
            rules={[{ required: true, message: "Please select a plan" }]}
          >
            <Select
              placeholder="Select plan"
              options={plans.map((plan) => ({
                label: `${plan.name} - ${
                  plan.billingModel === "FIXED_MONTHLY"
                    ? `€${plan.basePrice}/month`
                    : `€${plan.basePrice} + €${plan.pricePerEmployee}/employee`
                }`,
                value: plan._id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="startDate"
            label="Start Date"
            rules={[{ required: true, message: "Please select start date" }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
          </Form.Item>

          {selectedSubscription && (
            <Form.Item name="status" label="Status">
              <Select
                options={[
                  { label: "Trial", value: "TRIAL" },
                  { label: "Active", value: "ACTIVE" },
                  { label: "Paused", value: "PAUSED" },
                  { label: "Cancelled", value: "CANCELLED" },
                ]}
              />
            </Form.Item>
          )}

          <Form.Item
            name="customMonthlyPrice"
            label="Custom Monthly Price (Optional)"
          >
            <Input type="number" placeholder="Override plan price" prefix="€" />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Add any notes..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Start Date Modal */}
      <Modal
        title="Change Subscription Start Date"
        open={isStartDateModalVisible}
        onOk={handleChangeStartDate}
        onCancel={() => setIsStartDateModalVisible(false)}
        okText="Change Start Date"
      >
        <p style={{ marginBottom: 16, color: "#ff4d4f" }}>
          ⚠️ <strong>Warning:</strong> Changing the start date will:
        </p>
        <ul style={{ marginBottom: 16, paddingLeft: 20 }}>
          <li>Delete all UNPAID and PENDING billing periods</li>
          <li>Keep PAID periods as payment history</li>
          <li>Regenerate new billing periods from the new start date</li>
        </ul>
        <Form form={startDateForm} layout="vertical">
          <Form.Item
            name="startDate"
            label="New Start Date"
            rules={[
              { required: true, message: "Please select new start date" },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD.MM.YYYY"
              placeholder="Select new start date"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionsManagement;
